import { useMemo, useState, type JSX } from 'react';
import { Box, Divider, styled } from '@mui/material';
import { SquareArrowOutUpRightIcon } from 'lucide-react';
import { ScrollableSection } from '@/components/ScrollableSection/ScrollableSection';
import { corePalette } from '@/themes/colours';
import {
  POPUP_BIOMARKER_COLUMNS,
  POPUP_BLOCK_PADDING,
  POPUP_BURDEN_COLUMNS,
  POPUP_MAX_HEIGHT,
  POPUP_SAMPLES_MAX_HEIGHT,
  TIMELINE_POPUP_WIDTH,
} from '@/constants/PatientDashboard/patientTimeline';
import type { ITimelinePopupContent } from '@/types/PatientDashboard/PatientTimeline.types';
import { formatDisplayDate, getEventPopupLabel } from './PatientTimeline.util';
import { MarkerDot } from './MarkerDotCluster.styles';
import { ExternalLink, PopupRow, PopupRowText } from './TimelinePopup.styles';
import { groupSamplesByLineage } from './TimelinePopup.util';
import TimelinePopupBiomarkerRow from './TimelinePopupBiomarkerRow';
import TimelinePopupBurdenGroup from './TimelinePopupBurdenGroup';
import TimelinePopupReportGroup from './TimelinePopupReportGroup';
import TimelinePopupSampleGroup from './TimelinePopupSampleGroup';
import TimelinePopupSection from './TimelinePopupSection';

const CURATION_LABEL = 'Go to Curation';
const CURATION_PADDING = '12px';

const Card = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: TIMELINE_POPUP_WIDTH,
  maxHeight: POPUP_MAX_HEIGHT,
  background: corePalette.white,
  borderRadius: '8px',
  boxShadow: '0px 0px 8px rgba(39,57,87,0.08), 0px 16px 16px rgba(39,57,87,0.16)',
  color: corePalette.offBlack100,
});

// The date and the curation footer stay put, so only the sections between them scroll.
const Sections = styled(ScrollableSection)({
  flex: 1,
  minHeight: 0,
});

const Samples = styled(ScrollableSection)({
  maxHeight: POPUP_SAMPLES_MAX_HEIGHT,
  '& .simplebar-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
});

const PopupDate = styled(PopupRowText)({
  padding: POPUP_BLOCK_PADDING,
  color: corePalette.grey100,
});

// The footer is a block of its own, so it owns its padding rather than the link doing it.
const CurationLink = styled(ExternalLink)({
  padding: CURATION_PADDING,
});

// Same footprint as CurationLink, so the footer keeps its height with nowhere to go.
const InertCurationLink = styled(CurationLink.withComponent('span'))({
  color: corePalette.grey100,
});

// The label follows whichever footer state wraps it: the link's green, or the inert grey.
const CurationLabel = styled(PopupRowText)({
  color: 'inherit',
});

interface IProps {
  content: ITimelinePopupContent;
  patientId?: string;
}

export default function TimelinePopup({ content, patientId }: IProps): JSX.Element {
  const {
    date, events, samples, reports, diseaseBurden, biomarkers, reportGroups, curationHref,
  } = content;
  const [expandedSampleId, setExpandedSampleId] = useState<string>();

  const sampleGroups = useMemo(() => groupSamplesByLineage(samples), [samples]);

  const handleToggleSampleGroup = (groupId: string): void => {
    setExpandedSampleId((current) => (current === groupId ? undefined : groupId));
  };

  const curationBody = (
    <>
      <CurationLabel>{CURATION_LABEL}</CurationLabel>
      <SquareArrowOutUpRightIcon aria-hidden />
    </>
  );

  return (
    <Card>
      <PopupDate>{formatDisplayDate(date)}</PopupDate>

      <Sections>
        {Boolean(events.length) && (
          <TimelinePopupSection title="Event">
            {events.map((event) => (
              <PopupRow key={event.id}>
                <MarkerDot style={{ background: event.colour }} />
                <PopupRowText truncate>{getEventPopupLabel(event.label, event.kind)}</PopupRowText>
              </PopupRow>
            ))}
          </TimelinePopupSection>
        )}

        {Boolean(samples.length) && (
          <TimelinePopupSection title="Samples">
            <Samples>
              {sampleGroups.map((group) => (
                <TimelinePopupSampleGroup
                  key={group.parent.id}
                  group={group}
                  isExpanded={expandedSampleId === group.parent.id}
                  onToggle={handleToggleSampleGroup}
                />
              ))}
            </Samples>
          </TimelinePopupSection>
        )}

        {Boolean(reports.length) && (
          <TimelinePopupSection title="Reports">
            {reports.map((report) => (
              <PopupRowText key={report.id} truncate>{report.label}</PopupRowText>
            ))}
          </TimelinePopupSection>
        )}

        {Boolean(reportGroups.length) && (
          <TimelinePopupSection title="Reports">
            {reportGroups.map((group) => (
              <TimelinePopupReportGroup key={group.type} group={group} patientId={patientId} />
            ))}
          </TimelinePopupSection>
        )}

        {Boolean(diseaseBurden.length) && (
          <TimelinePopupSection title="Disease burden" columns={POPUP_BURDEN_COLUMNS}>
            {diseaseBurden.map((group) => (
              <TimelinePopupBurdenGroup key={group.analyte} group={group} />
            ))}
          </TimelinePopupSection>
        )}

        {Boolean(biomarkers.length) && (
          <TimelinePopupSection title="Biomarker" columns={POPUP_BIOMARKER_COLUMNS}>
            {biomarkers.map((row) => (
              <TimelinePopupBiomarkerRow key={row.key} row={row} />
            ))}
          </TimelinePopupSection>
        )}
      </Sections>

      <Divider />
      {curationHref ? (
        <CurationLink href={curationHref} target="_blank" rel="noopener noreferrer">
          {curationBody}
        </CurationLink>
      ) : (
        <InertCurationLink aria-disabled="true">
          {curationBody}
        </InertCurationLink>
      )}
    </Card>
  );
}
